import { useQuery } from "@tanstack/react-query";
import { useApi, useInvalidateOn, type SlaClock } from "@ncp/api-client";

export interface Kpi { key: string; value: number; target?: number | null; unit: string; higherIsBetter: boolean; basis: string; trend: number[] }
export interface WatchlistItem { caseId: string; caseNumber: string; title: string; titleAr?: string | null; type: string; state: string; stateLabel: string; stateLabelAr: string; riskScore?: number | null; priority: string; assigneeName?: string | null; assigneeNameAr?: string | null; primaryClock?: SlaClock | null; pendingProposals: number; updatedAt: string; ownerRole?: string | null; unattended?: boolean }
export interface Dashboard {
  generatedAt: string;
  openCases: number;
  openByType: Record<string, number>;
  openByStatus: Record<string, number>;
  kpis: Kpi[];
  ecFunnel: Array<{ key: string; label: string; labelAr: string; count: number }>;
  intakeTrend: Array<{ week: string; byType: Record<string, number> }>;
  slaHeatmap: { xLabels: string[]; yLabels: string[]; yLabelsAr?: string[] | null; values: number[][] };
  watchlist: WatchlistItem[];
  decisionOutcomes: Record<string, number>;
  proposalDecisions: Record<string, number>;
  agents: Array<{ agentKey: string; agentName: string; runs: number; proposals: number; approved: number; edited: number; rejected: number; tokens: number }>;
  recentActivity: Array<{ id: string; caseId: string; caseNumber: string; occurredAt: string; kind: string; title: string; titleAr?: string | null; actorName?: string | null; actorNameAr?: string | null; actorKind: string }>;
  dealWatch: { openCandidates: number; failureToNotify: number; standstillBreach: number; watching: number; escalated: number; linked: number; dismissed: number; newSinceYesterday: number; lastSyncAt?: string | null; mode: string };
  news: { total: number; today: number; mergermarket: number; lastSyncAt?: string | null };
  /** Merger files (test files excluded) by the state they are in now. */
  ecByState?: Record<string, number> | null;
}

export interface NextStep { trigger: string; label: string; labelAr?: string | null; permitted: boolean }
export interface WorkItem {
  caseId: string; caseNumber: string; title: string; titleAr?: string | null; type: string; state: string; stateLabel: string; stateLabelAr: string; status: string; priority: string; riskScore?: number | null;
  assigneeId?: string | null; assigneeName?: string | null; assigneeNameAr?: string | null; ownerRole?: string | null; primaryClock?: SlaClock | null; pendingProposals: number; nextSteps: NextStep[];
}
export interface Workload { userId?: string | null; name?: string | null; nameAr?: string | null; open: number; atRisk: number; breached: number }
export interface MyWork { generatedAt: string; supervisor: boolean; myCases: WorkItem[]; awaitingDecision: WorkItem[]; approaching: WorkItem[]; unassignedPriority: WorkItem[]; workload: Workload[]; pendingProposals: number }

const LIVE_EVENTS = ["case.updated", "case.transitioned", "proposal.created", "proposal.updated", "sla.sweep", "dealwatch.updated"];

export function useDashboard() {
  const api = useApi();
  useInvalidateOn(LIVE_EVENTS, [["dashboard"]]);
  return useQuery({ queryKey: ["dashboard"], queryFn: () => api.get<Dashboard>("/api/dashboard/summary"), refetchInterval: 60_000 });
}

export function useMyWork() {
  const api = useApi();
  useInvalidateOn(LIVE_EVENTS, [["my-work"]]);
  return useQuery({ queryKey: ["my-work"], queryFn: () => api.get<MyWork>("/api/dashboard/my-work"), refetchInterval: 60_000 });
}
